import Link from 'next/link'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { useAuth } from '../context/AuthContext'



export default function Home() {
  const { loading, isAuthenticated } = useAuth()
  const router = useRouter()

  // Redirect authenticated users to /report
  useEffect(() => {
    if (!loading && isAuthenticated) {
      router.push('/editor')
    }
  }, [loading, isAuthenticated, router])

  // Show loading state
  if (loading) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600'></div>
      </div>
    )
  }

  // Don't render the landing page if user is authenticated (redirect is in progress)
  if (isAuthenticated) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600'></div>
      </div>
    )
  }

  return (
    <div className='flex flex-col min-h-screen bg-white'>
      {/* Header Navigation */}
      <header className='bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50'>
        <div className='max-w-7xl mx-auto px-4 md:px-8'>
          <div className='flex justify-between items-center h-16 md:h-20'>
            {/* Logo */}
            <div className='flex items-center'>
              <Link href='/'>
                <a className='text-2xl md:text-3xl font-black text-[#4169E1] hover:text-indigo-600 transition-colors'>
                  의사의노트
                </a>
              </Link>
            </div>

            {/* Navigation Links */}
            <div className='flex items-center space-x-4 md:space-x-6'>
              <Link href='/signin'>
                <a className='bg-[#4169E1] hover:bg-indigo-600 text-white px-6 md:px-8 py-2 md:py-3 rounded-lg md:rounded-xl font-semibold text-sm md:text-base transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg'>
                  로그인
                </a>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className='flex-1'>
        {/* Hero Section */}
        <section className='relative bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-16 md:py-32 px-4 md:px-8 overflow-hidden'>
          {/* Background decorations */}
          <div className='absolute top-10 md:top-20 right-10 md:right-20 w-32 md:w-72 h-32 md:h-72 bg-blue-200 rounded-full opacity-10 blur-2xl md:blur-3xl'></div>
          <div className='absolute bottom-10 md:bottom-20 left-10 md:left-20 w-40 md:w-96 h-40 md:h-96 bg-indigo-200 rounded-full opacity-10 blur-2xl md:blur-3xl'></div>

          <div className='max-w-7xl mx-auto relative'>
            <div className='grid lg:grid-cols-2 gap-12 md:gap-20 items-center'>
              <div className='space-y-8 md:space-y-10'>
                <div className='space-y-6 md:space-y-8'>
                  <div className='inline-flex items-center px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-100 to-indigo-100 text-[#4169E1] rounded-full text-xs md:text-sm font-semibold border border-blue-200'>
                    <span className='text-lg md:text-2xl mr-2'>✨</span>
                    의료진을 위한 혁신적인 솔루션
                  </div>
                  <h1 className='text-4xl md:text-7xl font-black leading-tight text-gray-900'>
                    의사의 설명을
                    <br />
                    <span className='bg-gradient-to-r from-[#4169E1] to-indigo-600 bg-clip-text text-transparent'>
                      환자 손 안에
                    </span>
                  </h1>
                  <p className='text-lg md:text-2xl text-gray-600 leading-relaxed font-light'>
                    설명서를 만들고, 관리하고, 환자와 공유하세요.
                    <br />
                    <span className='font-semibold text-gray-800'>진료 시간은 줄이고, 환자 이해도는 높입니다.</span>
                  </p>
                </div>

                <div className='flex flex-col md:flex-row gap-4 md:gap-6'>
                  <Link href='/signup'>
                    <a className='w-full md:w-auto bg-gradient-to-r from-[#4169E1] to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-lg md:text-xl py-4 md:py-5 px-8 md:px-10 rounded-xl md:rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 font-semibold inline-flex items-center justify-center'>
                      무료로 사용하기
                      <svg
                        className='inline-block ml-2 md:ml-3 w-5 h-5 md:w-6 md:h-6'
                        fill='currentColor'
                        viewBox='0 0 20 20'
                      >
                        <path
                          fillRule='evenodd'
                          d='M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z'
                          clipRule='evenodd'
                        />
                      </svg>
                    </a>
                  </Link>
                </div>

                <div className='flex items-center justify-center md:justify-start space-x-6 md:space-x-10 text-xs md:text-sm text-gray-500 pt-4'>
                  {[
                    { icon: '✓', text: '무료 체험' },
                    { icon: '✓', text: '설치 불필요' },
                    { icon: '✓', text: '즉시 시작' },
                  ].map((item) => (
                    <div key={item.text} className='flex items-center space-x-1 md:space-x-2'>
                      <div className='w-4 h-4 md:w-6 md:h-6 bg-green-100 rounded-full flex items-center justify-center'>
                        <span className='text-green-600 font-bold text-xs'>{item.icon}</span>
                      </div>
                      <span className='font-medium'>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className='relative mt-12 lg:mt-0'>
                <div className='relative'>
                  {/* Background decorative elements */}
                  <div className='absolute -top-4 md:-top-8 -right-4 md:-right-8 w-16 md:w-32 h-16 md:h-32 bg-gradient-to-br from-blue-200 to-indigo-200 rounded-full opacity-30 blur-xl'></div>
                  <div className='absolute -bottom-6 md:-bottom-12 -left-6 md:-left-12 w-20 md:w-40 h-20 md:h-40 bg-gradient-to-br from-indigo-200 to-purple-200 rounded-full opacity-30 blur-xl'></div>

                  {/* Main video */}
                  <div className='relative w-full h-64 md:h-[600px] rounded-2xl md:rounded-3xl overflow-hidden shadow-xl md:shadow-2xl border border-gray-200'>
                    <video
                      className='w-full h-full object-cover transform hover:scale-105 transition-transform duration-700'
                      autoPlay
                      loop
                      muted
                      playsInline
                    >
                      <source src='/hero_section.mp4' type='video/mp4' />
                      {/* Fallback image if video fails to load */}
                      <Image
                        src='/empty.jpeg'
                        alt='Doctor using DoctorNotes with patient'
                        layout='fill'
                        objectFit='cover'
                        className='transform hover:scale-110 transition-transform duration-700'
                      />
                    </video>
                  </div>

                  {/* Floating stats cards */}
                  <div className='absolute -bottom-4 md:-bottom-8 -left-4 md:-left-8 bg-white rounded-xl md:rounded-2xl shadow-xl md:shadow-2xl p-4 md:p-8 border border-gray-100'>
                    <div className='flex items-center space-x-3 md:space-x-6'>
                      <div className='w-8 h-8 md:w-16 md:h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg md:rounded-2xl flex items-center justify-center'>
                        <svg className='w-4 h-4 md:w-8 md:h-8 text-green-600' fill='currentColor' viewBox='0 0 20 20'>
                          <path d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
                        </svg>
                      </div>
                      <div>
                        <div className='text-lg md:text-3xl font-black text-gray-900'>98%</div>
                        <div className='text-gray-600 text-xs md:text-base font-medium'>환자 만족도</div>
                      </div>
                    </div>
                  </div>

                  <div className='absolute -top-4 md:-top-8 -right-4 md:-right-8 bg-white rounded-xl md:rounded-2xl shadow-xl md:shadow-2xl p-3 md:p-6 border border-gray-100'>
                    <div className='flex items-center space-x-2 md:space-x-4'>
                      <div className='w-6 h-6 md:w-12 md:h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg md:rounded-xl flex items-center justify-center'>
                        <span className='text-sm md:text-2xl'>⚡</span>
                      </div>
                      <div>
                        <div className='text-lg md:text-2xl font-black text-gray-900'>50%</div>
                        <div className='text-gray-600 text-xs md:text-sm font-medium'>시간 단축</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Overview */}
        <section className='py-16 md:py-32 px-4 md:px-8 bg-gray-50'>
          <div className='max-w-7xl mx-auto'>
            <div className='text-center mb-12 md:mb-24'>
              <div className='inline-flex items-center px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-100 to-indigo-100 text-[#4169E1] rounded-full text-xs md:text-sm font-semibold mb-6 md:mb-8 border border-blue-200'>
                <span className='text-lg md:text-2xl mr-2'>🚀</span>
                설명 템플릿 활용
              </div>
              <h2 className='text-3xl md:text-6xl font-black mb-6 md:mb-10 text-gray-900 leading-tight'>
                진료 시간은 줄이고,
                <br />
                <span className='bg-gradient-to-r from-[#4169E1] to-indigo-600 bg-clip-text text-transparent'>
                  이해의 깊이는 높이고
                </span>
              </h2>
              <p className='text-lg md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed font-light'>
                원하는 템플릿을 불러와 환자 이해도를 빠르게 높여요.
                <br />
                <span className='font-semibold text-gray-800'>복잡한 의료 정보를 쉽고 명확하게 전달합니다.</span>
              </p>
            </div>

            {/* Template showcase */}
            <div className='relative mb-12 md:mb-24'>
              <div className='relative w-full h-60 md:h-[500px] rounded-2xl md:rounded-3xl overflow-hidden shadow-xl md:shadow-2xl border border-gray-200'>
                <Image
                  src='/img/landing/templates.svg'
                  alt='Clinical case templates'
                  layout='fill'
                  objectFit='cover'
                  className='hover:scale-105 transition-transform duration-700'
                />
              </div>
              <div className='absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-2xl md:rounded-3xl'></div>
            </div>

            {/* Feature cards */}
            <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10'>
              {[
                {
                  icon: '🔄',
                  title: '설명서 패턴 재사용',
                  description:
                    '자주 사용하는 설명을 템플릿으로 저장하고 반복 사용하세요. 매번 새로 작성할 필요가 없어요.',
                  image: '/img/landing/feature1.png',
                  color: 'from-blue-500 to-indigo-600',
                },
                {
                  icon: '📄',
                  title: 'PDF 출력 및 인쇄',
                  description: '디지털이 어려운 고령 환자를 위해 종이로 인쇄해서 전달할 수 있어요.',
                  image: '/img/landing/feature2.png',
                  color: 'from-emerald-500 to-teal-600',
                },
                {
                  icon: '👨‍👩‍👧‍👦',
                  title: '가족과 함께 공유',
                  description: '환자 가족에게도 설명서를 공유해서 불안을 덜고 이해도를 높여요.',
                  image: '/img/landing/feature3.png',
                  color: 'from-purple-500 to-pink-600',
                },
              ].map((feature, index) => (
                <div
                  key={index}
                  className='group bg-white rounded-2xl md:rounded-3xl p-6 md:p-10 shadow-lg hover:shadow-xl md:hover:shadow-2xl transition-all duration-500 border border-gray-100 transform hover:-translate-y-1 md:hover:-translate-y-2'
                >
                  <div className='relative h-40 md:h-56 mb-6 md:mb-8 rounded-xl md:rounded-2xl overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100'>
                    <Image
                      src={feature.image}
                      alt={feature.title}
                      layout='fill'
                      objectFit='contain'
                      className='group-hover:scale-110 transition-transform duration-500 p-3 md:p-4'
                    />
                  </div>
                  <div className='text-3xl md:text-5xl mb-4 md:mb-6'>{feature.icon}</div>
                  <h3 className='text-lg md:text-2xl font-bold mb-3 md:mb-4 text-gray-900'>{feature.title}</h3>
                  <p className='text-gray-600 leading-relaxed text-sm md:text-lg'>{feature.description}</p>
                  <div
                    className={`mt-4 md:mt-6 h-1 bg-gradient-to-r ${feature.color} rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500`}
                  ></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* KakaoTalk Integration */}
        <section className='py-16 md:py-32 px-4 md:px-8 bg-black'>
          <div className='max-w-7xl mx-auto'>
            <div className='grid lg:grid-cols-2 gap-12 md:gap-20 items-center'>
              <div className='space-y-8 md:space-y-10 order-2 lg:order-1'>
                <div className='inline-flex items-center px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-gray-800 to-gray-700 text-white rounded-full text-xs md:text-sm font-semibold border border-gray-600'>
                  <span className='text-lg md:text-2xl mr-2'>💬</span>
                  카카오톡 설명서 공유
                </div>
                <h2 className='text-3xl md:text-6xl font-black text-white leading-tight'>
                  진료 정보를
                  <br />
                  <span className='bg-gradient-to-r from-[#4169E1] to-indigo-400 bg-clip-text text-transparent'>
                    쉽고 빠르게
                  </span>{' '}
                  전달
                </h2>
                <p className='text-lg md:text-2xl text-gray-300 leading-relaxed font-light'>
                  카카오톡으로 설명서를 빠르게 공유해요.
                  <br />
                  <span className='font-semibold text-white'>환자가 언제든지 다시 확인할 수 있습니다.</span>
                </p>
                <div className='space-y-4 md:space-y-6'>
                  {[
                    { icon: '📱', text: '카카오톡으로 즉시 전송' },
                    { icon: '🔗', text: '링크 클릭만으로 간편 접속' },
                    { icon: '📋', text: '언제든지 다시 확인 가능' },
                    { icon: '👪', text: '가족과 쉽게 공유' },
                  ].map((feature) => (
                    <div key={feature.text} className='flex items-center space-x-3 md:space-x-4'>
                      <div className='w-6 h-6 md:w-8 md:h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center'>
                        <span className='text-sm md:text-lg'>{feature.icon}</span>
                      </div>
                      <span className='text-gray-300 text-base md:text-lg font-medium'>{feature.text}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className='relative order-1 lg:order-2'>
                <div className='relative w-full h-[500px] md:h-[700px] lg:h-[800px] mx-auto'>
                  <div className='w-full h-full rounded-3xl overflow-hidden shadow-xl md:shadow-2xl'>
                    <video className='w-full h-full object-contain' autoPlay loop muted playsInline>
                      <source src='/introdiction_features.mp4' type='video/mp4' />
                      {/* Fallback content if video fails to load */}
                      <div className='flex items-center justify-center h-full bg-gradient-to-br from-gray-800 to-gray-900'>
                        <div className='text-center space-y-4 md:space-y-6'>
                          <div className='w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto'>
                            <span className='text-2xl md:text-3xl'>💬</span>
                          </div>
                          <div className='text-white text-lg md:text-xl font-semibold'>기능 설명 영상</div>
                          <div className='text-gray-400 text-sm md:text-base'>동영상을 지원하지 않는 브라우저</div>
                        </div>
                      </div>
                    </video>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className='py-12 md:py-24 px-4 md:px-8 bg-gradient-to-r from-[#4169E1] to-indigo-600'>
          <div className='max-w-7xl mx-auto'>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12 text-white text-center'>
              {[
                { number: '10,000+', label: '누적 설명서', suffix: '개' },
                { number: '98%', label: '환자 만족도', suffix: '' },
                { number: '50%', label: '진료시간 단축', suffix: '' },
                { number: '24/7', label: '언제든지 접근', suffix: '' },
              ].map((stat) => (
                <div key={stat.label} className='space-y-2 md:space-y-4'>
                  <div className='text-2xl md:text-5xl font-black'>
                    {stat.number}
                    {stat.suffix}
                  </div>
                  <div className='text-blue-100 text-sm md:text-lg font-medium'>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className='py-16 md:py-32 px-4 md:px-8 bg-gradient-to-br from-[#4169E1] via-indigo-600 to-purple-700 relative overflow-hidden'>
          <div className='absolute top-0 left-0 w-full h-full opacity-10'>
            <div className='absolute top-10 md:top-20 left-10 md:left-20 w-32 md:w-96 h-32 md:h-96 bg-white rounded-full blur-2xl md:blur-3xl'></div>
            <div className='absolute bottom-10 md:bottom-20 right-10 md:right-20 w-28 md:w-80 h-28 md:h-80 bg-white rounded-full blur-2xl md:blur-3xl'></div>
          </div>
          <div className='max-w-5xl mx-auto text-center text-white relative space-y-8 md:space-y-12'>
            <h2 className='text-3xl md:text-6xl font-black leading-tight'>
              진료실 너머 환자의 삶을 위해,
              <br />
              <span className='text-blue-200'>오늘부터 시작해보세요</span>
            </h2>
            <p className='text-lg md:text-2xl text-blue-100 font-light'>
              무료 체험으로 의사의노트의 혁신을 경험해보세요
            </p>
            <div className='flex flex-col md:flex-row gap-4 md:gap-8 justify-center'>
              <Link href='/signup'>
                <a className='w-full md:w-auto bg-white text-[#4169E1] hover:bg-gray-100 rounded-xl md:rounded-2xl py-4 md:py-6 px-8 md:px-12 text-lg md:text-xl font-bold shadow-xl md:shadow-2xl hover:shadow-2xl md:hover:shadow-3xl transition-all transform hover:scale-105 inline-flex items-center justify-center'>
                  무료로 사용하기
                  <svg
                    className='inline-block ml-2 md:ml-3 w-5 h-5 md:w-6 md:h-6'
                    fill='currentColor'
                    viewBox='0 0 20 20'
                  >
                    <path
                      fillRule='evenodd'
                      d='M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z'
                      clipRule='evenodd'
                    />
                  </svg>
                </a>
              </Link>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className='py-12 md:py-24 px-4 md:px-8 bg-gradient-to-r from-[#E17C41] to-orange-600'>
          <div className='max-w-5xl mx-auto text-center space-y-6 md:space-y-8'>
            <h2 className='text-2xl md:text-5xl font-black text-white leading-tight'>
              아직 망설여지시나요?
              <br />
              <span className='text-orange-100'>먼저 짧은 대화를 나눠봐요</span>
            </h2>
            <p className='text-lg md:text-xl text-orange-100 font-light'>궁금한 점이 있으시면 언제든지 문의해주세요</p>
            <br />
            <br />
            <a
              href='mailto:sakaki3412@gmail.com'
              className='w-full md:w-auto bg-white text-[#E17C41] hover:bg-gray-100 px-8 md:px-12 py-4 md:py-6 rounded-xl md:rounded-2xl text-lg md:text-xl font-bold shadow-xl md:shadow-2xl hover:shadow-2xl md:hover:shadow-3xl transition-all transform hover:scale-105'
            >
              문의하기
              <svg className='inline-block ml-2 md:ml-3 w-5 h-5 md:w-6 md:h-6' fill='currentColor' viewBox='0 0 20 20'>
                <path
                  fillRule='evenodd'
                  d='M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z'
                  clipRule='evenodd'
                />
              </svg>
            </a>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className='bg-[#28282B] text-white py-12 md:py-20 px-4 md:px-8'>
        <div className='max-w-7xl mx-auto'>
          <div className='grid lg:grid-cols-4 gap-8 md:gap-16'>
            <div>
              <h4 className='font-bold mb-4 md:mb-8 text-lg md:text-xl'>회사 정보</h4>
              <div className='space-y-2 md:space-y-4 text-white/70 text-sm md:text-lg'>
                <p>대표 : 황상필</p>
                <p>사업자 번호 : 584-65-00467</p>
                <p>이메일 : sakaki3412@gmail.com</p>
                <p>전화 : 02-1234-5678</p>
              </div>
            </div>
          </div>

          <div className='border-t border-white/20 mt-8 md:mt-16 pt-6 md:pt-10 flex flex-col md:flex-row justify-between items-center'>
            <p className='text-white/50 text-sm md:text-lg'>© 2024 Pedicle. All Rights Reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
